import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { orderBy } from 'lodash';
import { RootState } from '../store';

export enum JobStatus {
  NotStarted,
  InProgress,
  Success,
  Failed,
}

export interface SubmittedJobDetails {
  status?: JobStatus;
  completedDate?: string;
  startedDate?: string;
  name?: string;
  id?: string;
  widgetId: string;
}

interface ExportState {
  submittedJobs: SubmittedJobDetails[];
  panelOpened: boolean;
}

const initialState: ExportState = {
  submittedJobs: [],
  panelOpened: false,
};

const runningJobsSlice = createSlice({
  name: 'runningJobs',
  initialState,
  reducers: {
    submitJob: (state, action: PayloadAction<SubmittedJobDetails>) => {
      const exists = state.submittedJobs.some(
        (job) => job.widgetId === action.payload.widgetId && job.status === JobStatus.InProgress
      );
      if (!exists) {
        state.submittedJobs.push({
          status: JobStatus.InProgress,
          ...action.payload,
        });
        state.panelOpened = true;
      }
    },
    setPanelOpened: (state, action: PayloadAction<boolean>) => {
      state.panelOpened = action.payload;
    },
    setJobStatus: (state, action: PayloadAction<Partial<SubmittedJobDetails>>) => {
      const job = state.submittedJobs.find(
        (job) => job.widgetId === action.payload.widgetId && job.startedDate === action.payload.startedDate
      );
      if (job) {
        job.status = action.payload.status ?? job.status;
        job.id = action.payload.id ?? job.id;
        job.name = action.payload.name ?? job.name;
        job.completedDate = action.payload.completedDate ?? job.completedDate;
      }
    },
    removeJob: (state, action: PayloadAction<Partial<SubmittedJobDetails>>) => {
      state.submittedJobs = state.submittedJobs.filter(
        (job) => !(job.widgetId === action.payload.widgetId && job.startedDate === action.payload.startedDate)
      );
    },
  },
});

// actions
export const { submitJob, setJobStatus, removeJob, setPanelOpened } = runningJobsSlice.actions;

// selectors
const _selectSubmittedJobs = ({ rootReducer: { runningJobs } }: RootState): SubmittedJobDetails[] =>
  runningJobs.submittedJobs;

const _selectPanelOpened = ({ rootReducer: { runningJobs } }: RootState): boolean => runningJobs.panelOpened;

export const selectSubmittedJobs = createSelector(_selectSubmittedJobs, (submittedJobs) => {
  return orderBy(
    submittedJobs,
    (job: SubmittedJobDetails) => {
      return new Date(job.completedDate ?? '');
    },
    'desc'
  );
});

export const selectHasRunningJobs = createSelector(_selectSubmittedJobs, (submittedJobs) => {
  return submittedJobs.some((job) => job.status === JobStatus.InProgress);
});

export const selectPanelOpened = createSelector(_selectPanelOpened, (panelOpened) => {
  return panelOpened;
});

// reducer
export default runningJobsSlice.reducer;
